// ===================================================================
// ℹ️ SOBRE — Empresa proprietária do sistema (somente leitura)
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Alert } from '../../components/ui/Alert';
import { apiClient } from '../../api/apiClient';
import { WhatsAppIcon } from '../../components/icons/WhatsAppIcon';

const DEFAULT_LOGO = '/static/ingressa-logo.svg';

function formatCnpjDisplay(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length !== 14) return raw || '—';
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function formatPhoneDisplay(raw) {
  const n = String(raw || '').replace(/\D/g, '');
  if (!n) return '—';
  if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  return raw;
}

export const AboutPage = React.memo(({ userRole }) => {
  const isMaster = userRole === 'master';
  const hideOwnerPhone = userRole === 'candidate';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const [data, setData] = useState({ legal_name: '', cnpj: '', phone: '', phone_whatsapp: false, icon: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.getPlatformAbout();
      setData(res);
      setPreviewError(false);
    } catch (err) {
      setError(err.message || 'Erro ao carregar informações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <LoadingSpinner text="Carregando..." />;
  }

  const showIcon = data.icon && (data.icon.startsWith('http') || data.icon.startsWith('/')) && !previewError;

  return (
    <div style={styles.contentContainer}>
      <div style={{ ...styles.card, maxWidth: 640, margin: '0 auto' }}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>ℹ️ Sobre</h2>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0' }}>
            Empresa proprietária da plataforma
          </p>
        </div>

        {error && <Alert type="error" message={error} />}

        {isMaster && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 14px',
              backgroundColor: '#EFF6FF',
              borderRadius: 8,
              border: '1px solid #BFDBFE',
              fontSize: 13,
              color: '#1E40AF',
            }}
          >
            Para alterar estes dados, use o menu <strong>Configurações</strong> (módulo proprietário).
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, padding: '8px 0 24px' }}>
          <div style={{ padding: 16, backgroundColor: '#F9FAFB', borderRadius: 12, border: '1px solid #E5E7EB' }}>
            {showIcon ? (
              <img
                src={data.icon}
                alt=""
                style={{ maxWidth: 140, maxHeight: 140, objectFit: 'contain', display: 'block' }}
                onError={() => setPreviewError(true)}
              />
            ) : (
              <img src={DEFAULT_LOGO} alt="" style={{ width: 96, height: 96, objectFit: 'contain', display: 'block', opacity: 0.75 }} />
            )}
          </div>
          <dl style={{ width: '100%', margin: 0 }}>
            <div style={{ marginBottom: 16 }}>
              <dt style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Razão social
              </dt>
              <dd style={{ margin: '6px 0 0', fontSize: 16, color: '#111827', fontWeight: 500 }}>
                {data.legal_name || '—'}
              </dd>
            </div>
            <div style={{ marginBottom: 16 }}>
              <dt style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                CNPJ
              </dt>
              <dd style={{ margin: '6px 0 0', fontSize: 16, color: '#111827', fontFamily: 'monospace' }}>
                {formatCnpjDisplay(data.cnpj)}
              </dd>
            </div>
            {!hideOwnerPhone && (
              <div>
                <dt style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Telefone
                </dt>
                <dd
                  style={{
                    margin: '6px 0 0',
                    fontSize: 16,
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>{formatPhoneDisplay(data.phone)}</span>
                  {data.phone_whatsapp && data.phone && String(data.phone).replace(/\D/g, '') && (
                    <a
                      href={`https://wa.me/55${String(data.phone).replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir conversa no WhatsApp"
                      style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
                    >
                      <WhatsAppIcon size={22} />
                    </a>
                  )}
                </dd>
              </div>
            )}
          </dl>
          {!data.legal_name && !data.cnpj && !data.icon && (hideOwnerPhone || !data.phone) && (
            <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', margin: 0 }}>
              Informações do proprietário ainda não foram cadastradas.
            </p>
          )}
        </div>
      </div>
    </div>
  );
});
